// frontend/src/pages/PatientAccessPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../utils/apiConfig';

function PatientAccessPage() {
  // Estado general
  const [cedula, setCedula] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('terminos'); // 'terminos' | 'verifyCedula' | 'verifyOtp' | 'accessGranted'
  const [mensaje, setMensaje] = useState('');
  const [documentId, setDocumentId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  // OTP helpers
  const [tiempoRestante, setTiempoRestante] = useState(0);
  const [codigoExpirado, setCodigoExpirado] = useState(false);
  const [metodosEnvio, setMetodosEnvio] = useState([]);
  const [reenviando, setReenviando] = useState(false);

  // Detección Web OTP
  const [soportaWebOTP, setSoportaWebOTP] = useState(false);
  const [buscandoAutoOTP, setBuscandoAutoOTP] = useState(false);

  // Query params (magic link)
  const [qpCedula, setQpCedula] = useState('');
  const [qpOtp, setQpOtp] = useState('');
  const [qpId, setQpId] = useState('');

  // Refs
  const navigate = useNavigate();
  const otpInputRef = useRef(null);
  const webOtpAbortRef = useRef(null);
  const countdownRef = useRef(null);

  // ---------- Utilidades ----------
  const formatearTiempo = (ms) => {
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const iniciarContador = useCallback((ms = 5 * 60 * 1000) => {
    setTiempoRestante(ms);
    setCodigoExpirado(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setTiempoRestante((prev) => {
        if (prev <= 1000) {
          clearInterval(countdownRef.current);
          setCodigoExpirado(true);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);
  }, []);

  const detenerContador = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // ---------- Query Params al cargar ----------
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const id = url.searchParams.get('id') || '';
      const otpQ = (url.searchParams.get('otp') || '').replace(/\D/g, '');
      const ced = (url.searchParams.get('cedula') || '').replace(/\D/g, '');
      if (id) setQpId(id);
      if (otpQ && otpQ.length === 6) setQpOtp(otpQ);
      if (ced) setQpCedula(ced);
    } catch {
      // Ignorar
    }
  }, []);

  // ---------- Compatibilidad Web OTP ----------
  useEffect(() => {
    setSoportaWebOTP('OTPCredential' in window);
  }, []);

  // ---------- Validar vigencia ----------
  const validarVigencia = useCallback(async (id) => {
    setIsLoading(true);
    setLoadingMessage('Verificando vigencia del enlace...');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/validar-vigencia/${id}`);
      const data = await resp.json();
      if (resp.ok) {
        setDocumentId(data.documentoId);
        return true;
      }
      if (resp.status === 410) {
        alert(data.message || 'El enlace ha expirado. Solicita uno nuevo.');
      } else if (resp.status === 404) {
        alert('Documento no encontrado.');
      } else {
        alert(data.message || 'Error al validar vigencia.');
      }
      return false;
    } catch {
      alert('Error de conexión al servidor.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ---------- Aceptar Términos ----------
  const handleAceptarTerminos = async () => {
    const id = qpId || new URLSearchParams(window.location.search).get('id');
    if (!id) {
      alert('❌ No se encontró el identificador del documento.');
      return;
    }
    const ok = await validarVigencia(id);
    if (!ok) return;

    // Si el magic link trae cedula + otp, intentamos validar DIRECTO (sin pedir OTP ni enviar SMS)
    if (qpCedula && qpOtp && qpOtp.length === 6) {
      try {
        setIsLoading(true);
        setLoadingMessage('Verificando acceso desde enlace...');
        const { ok: tienePendiente, id: pendId } = await verificarDocumentosPendientes(qpCedula);

        if (tienePendiente) {
          const res = await fetch(`${API_BASE_URL}/api/otp/validar-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cedula: qpCedula, codigo: qpOtp }),
          });
          const data = await res.json();
          if (res.ok) {
            setStep('accessGranted');
            setMensaje('Acceso autorizado. Redirigiendo...');
            setTimeout(() => {
              navigate(`/document-view/${qpCedula}`, {
  state: { cedula: qpCedula, documentoId: pendId || documentId },
});
            }, 600);
            setIsLoading(false);
            return; // Salimos: ya no necesitamos verifyCedula
          } else {
            // Si el OTP de la URL no valida, seguimos flujo normal
            setMensaje(data.error || 'El código del enlace no fue válido. Ingresa tu documento para continuar.');
          }
        }
      } catch {
        // Caemos al flujo normal
      } finally {
        setIsLoading(false);
      }
    }

    // Flujo normal: pasar a verificar cédula
    if (qpCedula) setCedula(qpCedula);
    setStep('verifyCedula');
  };

  // ---------- Verificar documentos pendientes ----------
  const verificarDocumentosPendientes = useCallback(async (ced) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/documento-por-cedula/${ced}`);
      const data = await res.json();

      // Aceptamos si el backend devuelve OK; obtenemos id si está.
      if (!res.ok) {
        alert('Usted no tiene documentos pendientes por firmar.');
        window.location.href = 'https://colombianadetrasplantes.com';
        return { ok: false, id: null };
      }

      const id = data._id || data?.documento?._id || data?.documentoId || null;
      return { ok: true, id };
    } catch {
      alert('Error al verificar documentos pendientes.');
      return { ok: false, id: null };
    }
  }, []);

  // ---------- Enviar OTP ----------
  const iniciarWebOTP = useCallback(() => {
    if (!('OTPCredential' in window) || step !== 'verifyOtp') return;
    try {
      if (webOtpAbortRef.current) webOtpAbortRef.current.abort();
      const ac = new AbortController();
      webOtpAbortRef.current = ac;

      setBuscandoAutoOTP(true);
      const timeout = setTimeout(() => ac.abort(), 120000); // 2 min

      (async () => {
        try {
          const cred = await navigator.credentials.get({
            otp: { transport: ['sms'] },
            signal: ac.signal,
          });
          if (cred?.code && cred.code.length === 6) {
            setOtp(cred.code);
            setTimeout(() => {
              const form = document.querySelector('form[data-otp-form]');
              if (form) form.requestSubmit();
            }, 150);
          }
        } catch {
          // cancel/timeout/denied → continuar sin bloquear
        } finally {
          clearTimeout(timeout);
          setBuscandoAutoOTP(false);
        }
      })();
    } catch {
      // Ignorar
    }
  }, [step]);

  const enviarOtp = useCallback(async (ced) => {
    setIsLoading(true);
    setLoadingMessage('Enviando código de verificación...');
    setMensaje('');
    try {
      const otpRes = await fetch(`${API_BASE_URL}/api/otp/enviar-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cedula: ced }),
      });
      const otpData = await otpRes.json();
      if (!otpRes.ok) {
        setMensaje(otpData.error || 'Error al enviar OTP.');
        setIsLoading(false);
        return false;
      }
      const metodos = otpData.metodos || [];
      setMetodosEnvio(metodos);
      iniciarContador();
      setMensaje(
        metodos.length
          ? `Código enviado por ${metodos.join(' y ')}.`
          : 'Código enviado. Si tu dispositivo lo permite, se detectará automáticamente.'
      );
      setStep('verifyOtp');

      // Inicia Web OTP inmediatamente tras enviar (mejor tasa de éxito)
      setTimeout(() => {
        try { iniciarWebOTP(); } catch {}
      }, 150);

      return true;
    } catch {
      setMensaje('Error de conexión con el servidor.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [iniciarContador, iniciarWebOTP]);

  // ---------- Submit Verificar Cédula ----------
  const handleVerifyCedula = async (e) => {
    e.preventDefault();
    if (!cedula) {
      setMensaje('Debes ingresar la cédula.');
      return;
    }
    setIsLoading(true);
    setLoadingMessage('Verificando cédula...');
    setMensaje('');

    const { ok, id } = await verificarDocumentosPendientes(cedula);
    setIsLoading(false);
    if (!ok) return;

    if (id) setDocumentId(id);
    await enviarOtp(cedula);
  };

  // ---------- Reenviar OTP ----------
  const handleReenviarOTP = async () => {
    setReenviando(true);
    setMensaje('Reenviando código...');
    try {
      const otpRes = await fetch(`${API_BASE_URL}/api/otp/reenviar-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cedula }),
      });
      const otpData = await otpRes.json();
      if (!otpRes.ok) {
        setMensaje(otpData.error || 'Error al reenviar OTP.');
        setReenviando(false);
        return;
      }
      const metodos = otpData.metodos || metodosEnvio;
      setMetodosEnvio(metodos);
      iniciarContador();
      setCodigoExpirado(false);
      setOtp('');
      setMensaje(
        metodos.length
          ? `Código reenviado por ${metodos.join(' y ')}.`
          : 'Código reenviado.'
      );

      // Reintenta Web OTP tras el reenvío
      setTimeout(() => {
        try { iniciarWebOTP(); } catch {}
      }, 150);
    } catch {
      setMensaje('Error de conexión con el servidor.');
    } finally {
      setReenviando(false);
    }
  };

  // ---------- Verificar OTP ----------
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) {
      setMensaje('Debes ingresar el código.');
      return;
    }
    if (codigoExpirado) {
      setMensaje('El código ha expirado. Solicita uno nuevo.');
      return;
    }

    setIsLoading(true);
    setLoadingMessage('Validando código...');
    setMensaje('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/otp/validar-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cedula, codigo: otp }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.expirado) {
          setCodigoExpirado(true);
          detenerContador();
          setMensaje('El código ha expirado. Puedes solicitar uno nuevo.');
        } else {
          setMensaje(data.error || 'Código incorrecto.');
        }
        setIsLoading(false);
        return;
      }

      setLoadingMessage('Acceso autorizado. Preparando...');
      setMensaje('Acceso autorizado. Redirigiendo...');
      setStep('accessGranted');

      setTimeout(async () => {
        try {
          const docRes = await fetch(`${API_BASE_URL}/api/documento-pendiente/${cedula}`);
          const docData = await docRes.json();
          if (!docRes.ok || !docData || !docData._id) {
            alert('No se pudo obtener el documento pendiente.');
            setIsLoading(false);
            return;
          }
          navigate(`/document-view/${cedula}`, {
  state: { cedula, documentoId: docData._id },
});
        } catch {
          alert('Error al obtener el documento pendiente.');
        } finally {
          setIsLoading(false);
        }
      }, 700);
    } catch {
      setMensaje('Error de conexión con el servidor.');
      setIsLoading(false);
    }
  };

  // ---------- Auto-focus OTP ----------
  useEffect(() => {
    if (step === 'verifyOtp' && otpInputRef.current) {
      setTimeout(() => {
        otpInputRef.current.focus();
        if (/Mobi|Android/i.test(navigator.userAgent)) {
          otpInputRef.current.click();
        }
      }, 300);
    }
  }, [step]);

  // ---------- Lanzar Web OTP al entrar a verifyOtp ----------
  useEffect(() => {
    if (step === 'verifyOtp') iniciarWebOTP();
    return () => {
      if (webOtpAbortRef.current) webOtpAbortRef.current.abort();
    };
  }, [step, iniciarWebOTP]);

  // ---------- Leer OTP desde URL en verifyOtp ----------
  useEffect(() => {
    if (step !== 'verifyOtp') return;
    if (qpOtp && qpOtp.length === 6) {
      setOtp(qpOtp);
      setTimeout(() => {
        const form = document.querySelector('form[data-otp-form]');
        if (form) form.requestSubmit();
      }, 200);
    }
  }, [step, qpOtp]);

  // ---------- Cleanup ----------
  useEffect(() => {
    return () => {
      detenerContador();
      if (webOtpAbortRef.current) webOtpAbortRef.current.abort();
    };
  }, [detenerContador]);

  // ---------- UI ----------
  const LoadingPopup = () => (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#fff', padding: 24, borderRadius: 10, textAlign: 'center',
        minWidth: 280
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          border: '4px solid #eee', borderTop: '4px solid #007bff',
          margin: '0 auto 16px', animation: 'spin 1s linear infinite'
        }} />
        <div style={{ fontWeight: 'bold' }}>{loadingMessage}</div>
      </div>
      <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const okMsg = mensaje.toLowerCase();
  const isOk = okMsg.includes('enviado') || okMsg.includes('autorizado');

  return (
    <div style={{ maxWidth: 640, margin: '40px auto', padding: 20, background: '#f8f9fa', borderRadius: 12 }}>
      {isLoading && <LoadingPopup />}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <img src="/logo1.png" alt="Logo 1" style={{ height: 56 }} />
        <img src="/logo2.png" alt="Logo 2" style={{ height: 56 }} />
      </div>

      <h1 style={{ textAlign: 'center', color: '#1a1e4b', marginBottom: 10 }}>Acceso a BioFirma CTX</h1>

      {mensaje && (
        <div
          role="status"
          style={{
            textAlign: 'center',
            color: isOk ? '#155724' : '#721c24',
            backgroundColor: isOk ? '#d4edda' : '#f8d7da',
            border: `1px solid ${isOk ? '#c3e6cb' : '#f5c6cb'}`,
            borderRadius: 8, padding: 10, marginBottom: 16
          }}
        >
          {mensaje}
        </div>
      )}

      {step === 'terminos' && (
        <div>
          <h3 style={{ color: '#1a1e4b' }}>Términos y Condiciones</h3>
          <div style={{
            maxHeight: 280, overflowY: 'auto', background: '#fff',
            border: '1px solid #ddd', borderRadius: 8, padding: 14, fontSize: 14, textAlign: 'justify'
          }}>
            <p><strong>1. Introducción:</strong> BioFirmaCTX permite firmar consentimientos y documentos médicos digitalmente.</p>
            <p><strong>2. Definiciones:</strong> Firma electrónica; Plataforma BioFirmaCTX; Usuario; Firmante.</p>
            <p><strong>3. Funcionalidades:</strong> Aceptar términos, validar identidad, visualizar y firmar documentos, recibir copia por correo.</p>
            <p><strong>4. Protección de Datos:</strong> Conforme a Ley 1581 de 2012. Continuar implica autorización para el tratamiento de datos con fines de validación y firma.</p>
            <p><strong>5. Deberes del Usuario:</strong> Usar correctamente la plataforma y verificar la información antes de firmar.</p>
            <p><strong>6. Responsabilidad:</strong> No respondemos por fallas externas o de conectividad. Términos sujetos a cambios.</p>
            <p><strong>7. Aceptación:</strong> Al aceptar, consientes en validar identidad mediante OTP/selfie y recibir copia del documento.</p>
          </div>

          <div className="text-center" style={{ marginTop: 12 }}>
            <button className="btn btn-success" onClick={handleAceptarTerminos} disabled={isLoading}>
              {isLoading ? 'Verificando...' : 'Acepto'}
            </button>
            <button
              className="btn btn-danger"
              style={{ marginLeft: 8 }}
              onClick={() => (window.location.href = 'https://colombianadetrasplantes.com')}
            >
              No Acepto
            </button>
          </div>
        </div>
      )}

      {step === 'verifyCedula' && (
        <div>
          <form onSubmit={handleVerifyCedula}>
            <label className="fw-bold">Número de Documento</label>
            <input
              type="tel"
              value={cedula}
              onChange={(e) => setCedula(e.target.value.replace(/\D/g, ''))}
              required
              disabled={isLoading}
              className="form-control"
              placeholder="Ingrese su número de identificación"
              style={{ fontSize: 18, padding: 12, textAlign: 'center', letterSpacing: 1, margin: '10px 0 14px' }}
            />
            <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
              {isLoading ? 'Verificando...' : 'Verificar Cédula'}
            </button>
          </form>
        </div>
      )}

      {step === 'verifyOtp' && (
        <div>
          {soportaWebOTP && (
            <div style={{ textAlign: 'center', marginBottom: 10 }}>
              <span style={{
                display: 'inline-block',
                background: '#e3f2fd',
                border: '1px solid #90caf9',
                color: '#1565c0',
                borderRadius: 20,
                padding: '6px 12px',
                fontSize: 14
              }}>
                {buscandoAutoOTP ? '🔍 Buscando código en tu SMS...' : 'Si recibes el SMS, el código puede llenarse solo'}
              </span>
            </div>
          )}

          <form onSubmit={handleVerifyOtp} data-otp-form>
            <label className="fw-bold" style={{ fontSize: 16 }}>Código de verificación (6 dígitos)</label>
            <input
              ref={otpInputRef}
              type="tel"
              value={otp}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '');
                setOtp(v);
                if (v.length === 6 && !codigoExpirado) {
                  setTimeout(() => {
                    const form = document.querySelector('form[data-otp-form]');
                    if (form) form.requestSubmit();
                  }, 150);
                }
              }}
              required
              disabled={isLoading || codigoExpirado}
              className="form-control"
              placeholder="000000"
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              style={{
                fontSize: otp.length ? 28 : 24,
                color: otp.length === 6 ? '#28a745' : '#333',
                fontWeight: otp.length === 6 ? 'bold' : 'normal',
                letterSpacing: 8,
                textAlign: 'center',
                margin: '10px 0 12px',
                padding: 12
              }}
            />

            {tiempoRestante > 0 && !codigoExpirado && (
              <div style={{
                textAlign: 'center', marginBottom: 12,
                background: tiempoRestante < 60000 ? '#f8d7da' : tiempoRestante < 120000 ? '#fff3cd' : '#d1ecf1',
                border: `1px solid ${tiempoRestante < 60000 ? '#f5c6cb' : tiempoRestante < 120000 ? '#ffeeba' : '#bee5eb'}`,
                borderRadius: 8, padding: 10
              }}>
                <strong>Tiempo restante: {formatearTiempo(tiempoRestante)}</strong>
              </div>
            )}

            {codigoExpirado && (
              <div style={{
                textAlign: 'center', marginBottom: 12,
                background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 8, padding: 12, color: '#721c24'
              }}>
                El código ha expirado. Solicita uno nuevo.
              </div>
            )}

            <div className="d-flex gap-2">
              {!codigoExpirado ? (
                <button
                  type="submit"
                  disabled={isLoading || otp.length < 6}
                  className="btn btn-primary w-100"
                >
                  {isLoading ? 'Validando...' : (otp.length < 6 ? `Ingresa ${6 - otp.length} dígitos` : 'Verificar Código')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleReenviarOTP}
                  disabled={reenviando}
                  className="btn btn-warning w-100"
                >
                  {reenviando ? 'Reenviando...' : 'Solicitar nuevo código'}
                </button>
              )}
            </div>

            {soportaWebOTP && !codigoExpirado && (
              <div className="text-center" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={iniciarWebOTP}
                  disabled={buscandoAutoOTP}
                >
                  {buscandoAutoOTP ? 'Buscando en SMS...' : 'Detectar código automáticamente'}
                </button>
              </div>
            )}

            {!codigoExpirado && (
              <div className="text-center" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  onClick={handleReenviarOTP}
                  disabled={reenviando}
                  className="btn btn-link"
                >
                  {reenviando ? 'Reenviando...' : '¿No llegó el código? Reenviar'}
                </button>
              </div>
            )}

            <div style={{
              marginTop: 16, background: '#fff', border: '1px solid #eee',
              borderRadius: 8, padding: 12, fontSize: 14
            }}>
              <strong>Ayuda:</strong>
              <ul style={{ margin: '8px 0 0 18px' }}>
                <li>Si te llega un SMS, el código puede colocarse solo.</li>
                <li>Si abres un enlace con código (WhatsApp/Correo), se completará automáticamente.</li>
                <li>También puedes escribir los 6 números manualmente.</li>
              </ul>
            </div>
          </form>
        </div>
      )}

      {step === 'accessGranted' && (
        <div className="text-center mt-4 alert alert-success fw-bold">
          <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
          <div style={{ fontSize: 18, marginBottom: 4 }}>Acceso autorizado</div>
          <div style={{ fontSize: 14 }}>Redirigiendo al siguiente paso...</div>
        </div>
      )}
    </div>
  );
}

export default PatientAccessPage;
